import React from 'react'
import { Box, useTheme } from '@mui/material'
import { SplitControl } from '@repo/ui'
import { ArrowDownUp, ListFilter } from 'lucide-react'

import {
  toolbarControlSx,
  toolbarControlFieldRowSx,
  TOOLBAR_ICON_SIZE,
  FIELD_RADIUS_MAX,
} from 'components/UI/fieldMetrics'
import { CountPill, countedLabel } from 'components/UI/CountBadge'

/**
 * Stroke weight for every glyph in a toolbar control row.
 *
 * 1, not lucide's default 2. This is the house weight — working-conventions §21
 * states it outright ("use a lucide glyph at `strokeWidth={1}`"), and the
 * platform passes `strokeWidth={1}` at every toolbar call site. This app was
 * taking the library default, so the same `ArrowDownUp` rendered at DOUBLE the
 * line weight here: measured `stroke-width` 2 against the platform's 1, which
 * is why the row read as a heavier, blunter design language than the console it
 * is meant to match.
 *
 * Exported so the Layout button — which lives in `HeaderBar`, not here — sets
 * the same weight. A row with one hairline glyph and two heavy ones is worse
 * than a uniformly heavy row, so this must move as a unit.
 *
 * It lives here rather than beside `TOOLBAR_ICON_SIZE` in `fieldMetrics` only
 * because that file was out of scope for this change; the two belong together
 * and should be reunited when it is next touched.
 */
export const TOOLBAR_ICON_STROKE = 1

/**
 * THE list-toolbar Filter and Sort controls. One implementation, every surface.
 *
 * ## Why this exists
 *
 * `apps/app` had TWO hand-rolls of the same trio — `HeaderBar` (the certificates
 * list) and `SearchFilterSortBar` (the Provenance / Scans / Activity sub-views) —
 * and they had drifted in every dimension that is visible. Measured at a 1280
 * viewport, on a clean tree:
 *
 * | | certificates list | Provenance |
 * |---|---|---|
 * | Sort button   | 90 x 48 | **82 x 49** |
 * | Filter button | 95 x 48 | **87 x 49** |
 * | accessible name | `Sort` | **`Sort scans`** |
 *
 * The `Sort scans` accessible name was worse than cosmetic: `SearchFilterSortBar`
 * hardcoded the word "scans" into both labels, so a screen reader on the
 * PROVENANCE screen announced "Sort scans" over a list of owners. The subject is
 * now a prop with no default that names a surface, so a new caller cannot inherit
 * another screen's noun by accident.
 *
 * ## The platform is the spec, and it is a SPLIT BUTTON
 *
 * These controls are meant to read as `apps/platform`'s. They did not, and the
 * gap was structural rather than a matter of tint. Driven side by side at 1280
 * (platform Certificates on a preview whose `Sort.tsx`, `ActionLine.tsx` and
 * `@repo/ui` `Button` are byte-identical to `main`), with a sort applied:
 *
 * | | platform | this app, before |
 * |---|---|---|
 * | applied control | ONE 178 x 49 surface | TWO buttons, **12px apart** |
 * | gap sort -> clear | **0** | **12px** |
 * | clear segment | 48 wide, inner corners SQUARE | 58 wide, fully rounded |
 * | clear glyph | lucide `X`, 20px, **stroke 1** | MUI `CloseIcon`, 24px, **filled** |
 * | count | 22px pill **inside** the button | 18px disc overlaying the corner |
 * | glyph weight | **stroke 1** | **stroke 2** (lucide's default) |
 * | label | 16px / 500 | 14px / 400 |
 *
 * The first row is the one that actually read as broken. With a 12px gap on both
 * sides and the same border, fill and radius as its neighbours, the clear-X was
 * not an affordance attached to Sort — it was a fourth toolbar button sitting
 * between Sort and Layout, and an unlabelled one. The platform instead builds
 * `[icon Sort (n)][X]` as a single segmented surface, so the X is unmistakably
 * part of the control it clears.
 *
 * The platform does this in BOTH toolbar controls, from two independent call
 * sites that agree exactly (`input/Sort/Sort.tsx` and
 * `input/filters/Filters.tsx`): inline pill, then an attached 48px clear half.
 * That is the pattern reproduced here, so Filter and Sort stay siblings.
 *
 * ## What is deliberately NOT copied
 *
 * - **Height 48, not the platform's 49.** Per-band by design (§29): these sit
 *   beside this app's 48px search field, the platform's beside its 49px one.
 *   Matching the platform here would break the pairing that the number exists
 *   to serve.
 * - **Radius 6, not the platform's 8.** `FIELD_RADIUS_MAX` clamps field-shaped
 *   controls to 6 in this app, and these are field-adjacent by construction
 *   (see `toolbarControlSx`). Taking 8 would leave the toolbar rounder than the
 *   search box it sits next to.
 * - **The pill's COLOUR on a themed tenant.** It takes that brand's own colour
 *   rather than Vera purple; the platform's gradient is kept for the base Vera
 *   theme, where there is no tenant to defer to. See `CountBadge`.
 *
 * ## What is deliberately ADDED — motion the platform has none of
 *
 * The platform's control is static: the pill and the clear segment are simply
 * present or absent. Both animate here. The applied/cleared transition is the
 * one moment this control changes shape, and doing it instantly reads as a
 * glitch rather than a response. The pill pops (`CountBadge`) and the clear
 * segment slides out from under the sort button (`CLEAR_SLIDE_MS`) on the same
 * 220ms curve, because they are one event.
 *
 * Fill (#fff vs #fafafa) and border (#e4e4e7 vs #e7e7e7) also differ, by one to
 * three RGB steps. Measured, named, and left alone: below the threshold of
 * perception, and each is this app's own token.
 *
 * ## Geometry is NOT parameterised
 *
 * No `size`, no `height`, no `sx` passthrough. Every such prop is a place the
 * next divergence gets introduced, and the divergence above is what this file
 * exists to end. If the toolbar control needs to change size, it changes in
 * `toolbarControlSx` and moves everywhere at once.
 *
 * ## The split geometry lives in `@repo/ui` (working-conventions §47)
 *
 * It used to live in this file, and the header carried a note that its right
 * home was `packages/ui` "as the durable follow-up". Adding a clear affordance
 * to Filter is what forced it: building a second copy here would have been the
 * THIRD in the monorepo (the platform has one inline in each of `Sort.tsx` and
 * `Filters.tsx`), which is precisely what that note predicted.
 *
 * So `@repo/ui`'s `SplitControl` now owns the seam and the motion — the 1px
 * border overlap, squaring only the corners that touch, and the segment sliding
 * out from under the button — and this file owns only the app's SURFACE
 * (`toolbarControlSx`) and the two controls' content. Filter and Sort are
 * therefore the same control by construction rather than by two files agreeing.
 */

/**
 * Which HEIGHT BAND this control sits in — see `toolbarControlFieldRowSx`.
 *
 * `false` (the default) is the button-row band, `{ xs: 44, md: 48 }`. `true` is
 * the field-row band, a flat 48, for a control abreast of a
 * `ToolbarSearchField`. It is a boolean naming one of two real bands, NOT the
 * `size` / `height` / `sx` passthrough this file's header refuses — those are
 * open-ended, and an open-ended one is where the next divergence gets in.
 */
export type ToolbarBandProps = {
  fieldRow?: boolean
}

/*
  Takes `theme` because the band's border is the tenant's Studio "Border color"
  (`fieldBorderRest`), not a literal. It must stay a PLAIN OBJECT rather than
  becoming an `sx` theme-callback: `@repo/ui`'s `SplitControl` spreads it
  (`...controlSx`), and a callback spreads to `{}` and vanishes silently — the
  same trap `ToolbarSearchField` records at its own `sx`.
*/
const bandSx = (theme: any, fieldRow?: boolean) =>
  fieldRow ? toolbarControlFieldRowSx(theme) : toolbarControlSx(theme)

/** The visible label span — hidden on a phone, where the row has no width for it. */
const ToolbarLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component='span'
    sx={{
      ml: 1,
      whiteSpace: 'nowrap',
      // NO fontSize / fontWeight. This deliberately INHERITS the `@repo/ui`
      // Button's own label typography (16px / 500), which is exactly what the
      // platform's equivalent span does — it sets nothing but `display`.
      //
      // It used to be a `Typography` pinned to `14px / 400`, which is why the
      // same control read lighter and smaller here than on the platform. A
      // hand-rolled override of a shared component's typography is the
      // "rebuild sideways" §47 names; deleting it is what makes the two agree
      // by construction rather than by two numbers happening to match.
      //
      // Icon-only on a phone. The label is what forces these controls to eat
      // the row's whole width, and at 375px a labelled set leaves no room.
      // Desktop keeps the words: it has the space, and a pointer has no
      // tap-target pressure. `md` is 720 in this app, not MUI's 900.
      display: { xs: 'none', md: 'inline' },
    }}
  >
    {children}
  </Box>
)

export type FilterControlProps = ToolbarBandProps & {
  /** Applied-facet count. 0 renders no pill and no layout change. */
  count?: number
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void
  /**
   * What is being filtered, for the accessible name — "certificates", "scans",
   * "provenance". Required, and deliberately so: it is the prop that had a
   * hardcoded "scans" behind it on three screens.
   */
  subject: string
  disabled?: boolean
  /**
   * Clearing every applied filter.
   *
   * Supplied, Filter becomes a split control with a welded clear-X, exactly as
   * Sort already was — the platform builds BOTH of its toolbar controls that way
   * (`input/Sort/Sort.tsx` and `input/filters/Filters.tsx` agree exactly), and a
   * Filter that showed a count but offered no way to clear it made the user open
   * the sheet and untick facets one at a time to get back to unfiltered.
   *
   * Omitted, no segment is rendered or mounted and the geometry is unchanged, so
   * a surface with nothing to clear pays nothing for this.
   */
  onClear?: () => void
  showClear?: boolean
}

export const FilterControl: React.FC<FilterControlProps> = ({
  count = 0,
  onClick,
  subject,
  disabled,
  fieldRow,
  onClear,
  showClear = false,
}) => {
  const theme = useTheme()
  return (
  <SplitControl
    label={countedLabel(`Filter ${subject}`, count)}
    onClick={onClick}
    disabled={disabled}
    controlSx={bandSx(theme, fieldRow)}
    radius={FIELD_RADIUS_MAX}
    onClear={onClear}
    showClear={showClear}
    clearLabel={`Clear ${subject} filters`}
  >
    <ListFilter size={TOOLBAR_ICON_SIZE} strokeWidth={TOOLBAR_ICON_STROKE} />
    <ToolbarLabel>Filter</ToolbarLabel>
    <CountPill count={count} />
  </SplitControl>
)
}

export type SortControlProps = ToolbarBandProps & {
  /** Number of applied orderings. 0 renders no pill and no layout change. */
  count?: number
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void
  /** See `FilterControlProps.subject`. */
  subject: string
  /**
   * Whether the clear-sort affordance is OUT.
   *
   * The caller decides, because "is a sort applied" is page state. Note the
   * segment is additionally suppressed below `md` by `SplitControl` itself: on a
   * phone the sort sheet carries its own Clear, and a fourth control does not fit
   * the row.
   */
  showClear?: boolean
  onClear?: () => void
  /** The trigger element, for anchoring the menu that opens against it. */
  buttonRef?: React.Ref<HTMLButtonElement>
  disabled?: boolean
}

export const SortControl: React.FC<SortControlProps> = ({
  count = 0,
  onClick,
  subject,
  showClear = false,
  onClear,
  buttonRef,
  disabled,
  fieldRow,
}) => {
  const theme = useTheme()
  return (
  <SplitControl
    label={countedLabel(`Sort ${subject}`, count)}
    onClick={onClick}
    disabled={disabled}
    controlSx={bandSx(theme, fieldRow)}
    radius={FIELD_RADIUS_MAX}
    onClear={onClear}
    showClear={showClear}
    clearLabel='Clear sort'
    buttonRef={buttonRef}
  >
    <ArrowDownUp size={TOOLBAR_ICON_SIZE} strokeWidth={TOOLBAR_ICON_STROKE} />
    <ToolbarLabel>Sort</ToolbarLabel>
    <CountPill count={count} />
  </SplitControl>
)
}

export default SortControl
